import { useState, useEffect } from "react";
import Option from "../ui/option";
import Checkbox from "../ui/checkbox";
import Dropdown from '../ui/dropDown';
import CustomDropdown from '../ui/customDropDown';
// Helper function to get the correct ID (opt_id or id)
const getOptionId = (option) => option?.opt_id || option?.id;

export default function SubOptionModal({
  isOpen,
  onClose,
  activeOption,
  onSave,
}) {
  const [showValidation, setShowValidation] = useState(false);
  const [localSelection, setLocalSelection] = useState({});
  const [customQtyInputs, setCustomQtyInputs] = useState({});

  useEffect(() => {
    if (isOpen && activeOption) {
      const savedParent = activeOption.selected;
      // Load existing subSelections or start fresh
      setLocalSelection(savedParent?.subSelections || {});
      // Reset custom inputs
      setCustomQtyInputs({});
    }
  }, [isOpen, activeOption]);
  if (!isOpen || !activeOption) return null;
  const { parentOption } = activeOption;
  const subGroups = parentOption.sub_options;

  const handleSave = () => {
    const { groupId, parentOption, type } = activeOption;

    const mandatorySubs = (parentOption.sub_options || []).filter(
      (sub) => sub.mandatory
    );

    const missingMandatory = mandatorySubs.some((subGroup) => {
      const selectedVal = localSelection[subGroup.id];
      return (
        !selectedVal || (Array.isArray(selectedVal) && selectedVal.length === 0)
      );
    });

    if (missingMandatory) {
      setShowValidation(true);
      return;
    }

    setShowValidation(false);

    onSave({
      parent: parentOption,
      type,
      groupId,
      subSelections: localSelection,
    });
  };

  const isOptionSelected = (groupId, optionId) => {
    const selection = localSelection[groupId];
    if (!selection) return false;
    if (Array.isArray(selection)) {
      return selection.some(o => getOptionId(o) === optionId);
    }
    return getOptionId(selection) === optionId;
  };

  const getTotalQtyInSubGroup = (groupId) => {
    const selection = localSelection[groupId];
    if (!Array.isArray(selection)) return 0;
    return selection.reduce((sum, o) => sum + (o.qty || 0), 0);
  };

  const handleSubQtyChangeLocal = (groupId, opt, newQty) => {
    setLocalSelection(prev => {
      const group = parentOption.sub_options.find(g => g.id === groupId);
      const isRadio = group?.type === 'radio';

      // If it's a radio, just update the single object
      if (isRadio) {
        return {
          ...prev,
          [groupId]: { ...opt, qty: newQty }
        };
      }

      const existing = Array.isArray(prev[groupId]) ? prev[groupId] : [];
      const itemLimit = parseInt(group?.item_limit, 10);

      // If 5+ is selected set custom input flag with 6
      if (newQty === 6) {
        setCustomQtyInputs(prevInputs => ({
          ...prevInputs,
          [`${groupId}_${getOptionId(opt)}`]: true
        }));
      }

      // If item_limit is 0, no limit validation 
      if (itemLimit === 0 || isNaN(itemLimit)) {
        const found = existing.find(o => getOptionId(o) === getOptionId(opt));

        if (found) {
          return {
            ...prev,
            [groupId]: existing.map(o =>
              getOptionId(o) === getOptionId(opt) ? { ...o, qty: newQty } : o
            )
          };
        }

        return {
          ...prev,
          [groupId]: [...existing, { ...opt, qty: newQty }]
        };
      }

      //  item_limit > 0
      const otherTotal = existing
        .filter(o => getOptionId(o) !== getOptionId(opt))
        .reduce((s, o) => s + (o.qty || 0), 0);

      if (otherTotal + newQty > itemLimit) {
        return {
          ...prev,
          [groupId]: [{ ...opt, qty: newQty }],
        };
      }

      const found = existing.find(o => getOptionId(o) === getOptionId(opt));

      if (found) {
        return {
          ...prev,
          [groupId]: existing.map(o =>
            getOptionId(o) === getOptionId(opt) ? { ...o, qty: newQty } : o
          )
        };
      }

      return {
        ...prev,
        [groupId]: [...existing, { ...opt, qty: newQty }]
      };
    });
  };

  const handleCustomQtyInput = (groupId, opt, value) => {
    if (value === '') {
      setLocalSelection(prev => {
        const existing = Array.isArray(prev[groupId]) ? prev[groupId] : [];
        const found = existing.find(o => o.id === opt.id);

        if (found) {
          return {
            ...prev,
            [groupId]: existing.map(o =>
              getOptionId(o) === getOptionId(opt) ? { ...o, qty: '' } : o
            )
          };
        }
        return prev;
      });
      return;
    }
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      handleSubQtyChangeLocal(groupId, opt, numValue);
    }
  };

  const handleSelect = (group, opt, e) => {
    if (e?.target?.tagName === 'SELECT' || e?.target?.closest('select')) {
      return;
    }

    setShowValidation(false);

    if (group.type === "radio") {
      setLocalSelection((prev) => {
        const current = prev[group.id];
        if (current && current.id === opt.id) {
          const updated = { ...prev };
          delete updated[group.id];
          return updated;
        }
        return {
          ...prev,
          [group.id]: opt,
        };
      });
    } else if (group.type === "checkbox") {
      setLocalSelection(prev => {
        const existing = Array.isArray(prev[group.id]) ? prev[group.id] : [];
        const found = existing.find(o => getOptionId(o) === getOptionId(opt));
        const itemLimit = parseInt(group.item_limit, 10);

        if (found) {
          // Remove custom input flag when deselecting
          setCustomQtyInputs(prevInputs => {
            const updated = { ...prevInputs };
            delete updated[`${group.id}_${getOptionId(opt)}`];
            return updated;
          });

          return {
            ...prev,
            [group.id]: existing.filter(o => getOptionId(o) !== getOptionId(opt)),
          };
        }

        // If item_limit is 0, no limit validation
        if (itemLimit === 0 || isNaN(itemLimit)) {
          return {
            ...prev,
            [group.id]: [...existing, { ...opt, qty: 1 }],
          };
        }

        //  item_limit > 0
        const totalUsed = existing.reduce((s, o) => s + (o.qty || 0), 0);

        if (totalUsed + 1 > itemLimit) {
          return {
            ...prev,
            [group.id]: [{ ...opt, qty: 1 }],
          };
        }

        return {
          ...prev,
          [group.id]: [...existing, { ...opt, qty: 1 }],
        };
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-full m-10  rounded-[12px] p-10 shadow-xl max-h-[80vh] overflow-y-auto">
        <h2 className="md:text-[40px] text-[20px] font-outfit font-bold mb-10">{parentOption.name}</h2>

        {subGroups.map((group) => {
          const itemLimit = parseInt(group.item_limit, 10);
          const isUnlimited = itemLimit === 0 || isNaN(itemLimit);

          return (
            <div key={group.id} className="mb-6">
              <div
                className={`flex justify-between items-center mb-5 ${showValidation ? "border-2 border-boonsOrange rounded p-2" : ""
                  }`}
              >
                <p className="md:text-[30px] text-[20px] font-outfit font-semibold text-textgray">{group.title}</p>
                <span className="md:text-[30px] text-[20px] font-outfit font-semibold text-textgray">
                  {group.mandatory ? "Required" : "Optional"}
                </span>
              </div>

              {group.option.map((opt) => {
                const isSelected = isOptionSelected(group.id, getOptionId(opt));

                const totalUsed = getTotalQtyInSubGroup(group.id);
                const selectedOption = Array.isArray(localSelection[group.id])
                  ? localSelection[group.id].find(o => getOptionId(o) === getOptionId(opt))
                  : (getOptionId(localSelection[group.id]) === getOptionId(opt) ? localSelection[group.id] : null);

                const availableQty = isUnlimited
                  ? Infinity
                  : itemLimit - (totalUsed - (selectedOption?.qty || 0));

                const customInputKey = `${group.id}_${getOptionId(opt)}`;
                const isUsingCustomInput = customQtyInputs[customInputKey];

                return (
                  <button
                    key={opt.id}
                    onClick={(e) => handleSelect(group, opt, e)}
                    className={`w-full text-left border p-4 rounded-xl mb-5`}
                  >
                    <div className="flex justify-between items-center w-full">
                      <div className="flex-shrink-0 mr-4">
                        {group.type === "radio" && <Option selected={isSelected} />}
                        {group.type === "checkbox" && <Checkbox selected={isSelected} />}
                      </div>
                      <div className="capitalize flex-1 min-w-0 pr-4">
                        <p className="md:text-[30px] text-[20px] font-outfit font-semibold text-textgray truncate">
                          {opt.name}
                        </p>
                      </div>

                      <div className="flex items-center flex-shrink-0">
                        {/* Price Container - Fixed Width */}
                        <div className="w-[100px] flex justify-center mr-6 flex-shrink-0">
                          {opt.price > 0 && (
                            <div className="text-[20px] font-outfit font-semibold">
                              <p>${parseFloat(opt.price).toFixed(2)}</p>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-4 min-w-[150px] justify-end">


                          {/* Quantity control for checkbox with multiple selection */}
                          {group.type === 'checkbox' && group.allow_multiple_selection && (
                            <div className="min-w-[120px] flex justify-center">
                              {isUsingCustomInput ? (
                                <input
                                  type="number"
                                  min="6"
                                  placeholder="Qty"
                                  className="w-10 px-2 py-1 border border-gray-400 focus:outline-none rounded-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  value={selectedOption?.qty || ''}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  onFocus={(e) => {
                                    e.stopPropagation();
                                  }}
                                  onKeyDown={(e) => {
                                    e.stopPropagation();
                                  }}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleCustomQtyInput(group.id, opt, e.target.value);
                                  }}
                                />
                              ) : (
                                <div onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} onMouseDown={(e) => { e.stopPropagation(); }} className="w-20">
                                  <CustomDropdown
                                    variant="ghost"
                                    placeholder="Qty"
                                    disabled={!selectedOption || (!isUnlimited && availableQty <= 0)}
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleSubQtyChangeLocal(group.id, opt, Number(e.target.value));
                                    }}
                                    value={selectedOption?.qty ? selectedOption.qty : ''}
                                  >
                                    {isUnlimited
                                      ? [
                                        ...[1, 2, 3, 4, 5].map(n => (
                                          <option key={n} value={n}>{n}</option>
                                        )),
                                        <option key="6" value="6">5+</option>
                                      ] : (
                                        Array.from({ length: Math.max(0, parseInt(group.item_limit, 10)) }, (_, i) => (
                                          <option key={i + 1} value={i + 1}>
                                            {i + 1}
                                          </option>
                                        ))
                                      )}
                                  </CustomDropdown>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}

        <div className="flex justify-end gap-4 mt-4">
          <button
            onClick={() => {
              setShowValidation(false);
              setCustomQtyInputs({});
              onClose();
            }}
            className="px-4 py-2 bg-gray-300 rounded-lg"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}